import {
  FragmentType,
  getFragmentData,
} from '@/src/api/contentful/__generated__';
import { ShowableCardsFragmentFragment } from '@/src/api/contentful/__generated__/graphql';
import { useContentfulActionUrlMap } from '@/src/components/ActionCard/hooks/useContentfulActionUrlMap';
import { createCta } from '@/src/components/ActionCard/hooks/utils/createCta';
import { ShowableCardsFragment } from '@/src/components/ActionCard/hooks/utils/getShowableCards';
import { getDocumentAsReactNode } from '@/src/components/ActionCard/utils/getDocumentAsReactNode';
import { ActionCardBase } from '@/src/components/ActionCardBase/ActionCardBase';
import React from 'react';
import { Document } from '@contentful/rich-text-types';
import { getContentfulImageApiUrl } from '@/src/components/ActionCard/utils/getContentfulImageApiUrl';
import { ErrorNotification, LabelText, Margin } from '@ovotech/element-v5';

const ActionCard = ({
  cardContent,
}: {
  cardContent: ShowableCardsFragmentFragment['items'][number];
}) => {
  const { getActionByUrl } = useContentfulActionUrlMap();

  if (!cardContent || !cardContent.text?.header || !cardContent.actionCode)
    return null;

  const cta = createCta(
    cardContent.cta,
    cardContent.ctaBehaviour,
    getActionByUrl,
  );

  const inverted = !!cardContent.inverted;
  const body = getDocumentAsReactNode(
    cardContent.text?.text?.json as Document,
    inverted,
  );

  const image = {
    url: cardContent.image?.desktopImage?.url,
    label: cardContent.image?.altText || undefined,
    width: cardContent.image?.desktopImage?.width,
  };

  const imgSrc = getContentfulImageApiUrl(image.url, image.width);

  return (
    <ActionCardBase
      title={cardContent.text.header}
      body={body ? <>{body}</> : undefined}
      inverted={inverted}
      inline={!cardContent.nonRoundedEdges}
      imgSrc={imgSrc}
      onClickCta={cta?.action ? cta.action : undefined}
      ctaTitle={cta?.label || undefined}
    />
  );
};

type ActionCardListProps = {
  contentFragment?: FragmentType<typeof ShowableCardsFragment> | null;
};

export const ActionCardList = ({ contentFragment }: ActionCardListProps) => {
  const cardContent = getFragmentData(ShowableCardsFragment, contentFragment);

  if (!cardContent || cardContent?.items.length === 0) {
    return <ErrorNotification id="no-cards-found" title="No cards found" />;
  }

  return cardContent.items.map(item => (
    <Margin key={item?.sys.id} bottom={8}>
      <LabelText>{item?.actionCode}</LabelText>
      <ActionCard cardContent={item} />
    </Margin>
  ));
};
